import { Availability } from '../types';
import moment from 'moment/moment';
import { createDateRange } from '@repo/apg-ui';
import { endOfWeek, getWeek, setWeek, startOfWeek, startOfYear } from 'date-fns';

export const createAvailabilityDates = (start: string, end: string): Availability[] => {
  const startDate = moment(start);
  const endDate = moment(end);
  const days = endDate.diff(startDate, 'days') + 1; // +1 to include the end date

  const dates: Availability[] = [];
  for (let i = 0; i < days; i++) {
    const currentDate: moment.Moment = moment(start).add(i, 'days');
    const availabilityObject: Availability = {
      week: currentDate.isoWeek(),
      year: currentDate.year(),
      day: currentDate.weekday() + 1,
      date: currentDate.toISOString(),
      rating: 1,
      tooltip: '',
    };
    dates.push(availabilityObject);
  }
  return dates;
};

const getStartAndEndDateFromWeekNumber = (weekNumber, year) => {
  // Get the start of the year
  const startOfYearDate = startOfYear(new Date(year, 0, 1));
  // Set the week number
  const startDate = startOfWeek(setWeek(startOfYearDate, weekNumber), {
    weekStartsOn: 1,
  });
  // Get the end date of the week
  const endDate = endOfWeek(startDate, { weekStartsOn: 1 });

  return {
    startDate,
    endDate,
  };
};

export const createCalenderRangesFromDateRange = (startDate, duration = 7) => {
  const range = createDateRange(new Date(startDate), duration, {
    toEndOfYear: false,
    fullWeeks: false,
  });

  return [
    {
      range: [range],
      duration: duration,
      startDate: range.startDate,
      endDate: range.endDate,
    },
  ];
};

const SUMMER_SEASON_START_WEEK = 19;
const SUMMER_SEASON_END_WEEK = 44;
const WINTER_SEASON_START_WEEK = 45;
const WINTER_SEASON_END_WEEK = 18;

export const getDateRangeOfShortcut = (option: string, firstAvailableDate: string, lastAvailableDate: string) => {
  let today = new Date();
  const possibleStartDate = new Date(firstAvailableDate);
  const possibleEndDate = new Date(lastAvailableDate);
  const year = today.getFullYear();
  const currentWeek = getWeek(today);
  let startDate,
    endDate,
    duration,
    disabled = false;

  switch (option) {
    case 'summer_weeks':
      if (currentWeek < SUMMER_SEASON_START_WEEK) {
        ({ startDate, endDate } = getStartAndEndDateFromWeekNumber(SUMMER_SEASON_START_WEEK, year));
      } else if (currentWeek >= SUMMER_SEASON_START_WEEK && currentWeek < SUMMER_SEASON_END_WEEK) {
        ({ startDate, endDate } = getStartAndEndDateFromWeekNumber(currentWeek + 1, year));
      } else {
        ({ startDate, endDate } = getStartAndEndDateFromWeekNumber(SUMMER_SEASON_START_WEEK, year + 1));
      }
      duration = 7;
      break;
    case 'summer_season':
      if (
        currentWeek < SUMMER_SEASON_START_WEEK ||
        (currentWeek >= SUMMER_SEASON_START_WEEK && possibleEndDate.getFullYear() > year)
      ) {
        startDate = getStartAndEndDateFromWeekNumber(
          SUMMER_SEASON_START_WEEK,
          year + (currentWeek >= SUMMER_SEASON_START_WEEK ? 1 : 0),
        ).startDate;
        endDate = getStartAndEndDateFromWeekNumber(
          SUMMER_SEASON_END_WEEK,
          year + (currentWeek >= SUMMER_SEASON_START_WEEK ? 1 : 0),
        ).endDate;
        duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)); // Duration in days
      } else {
        disabled = true;
        duration = 0;
      }
      break;
    case 'winter_weeks':
      if (currentWeek < WINTER_SEASON_START_WEEK) {
        ({ startDate, endDate } = getStartAndEndDateFromWeekNumber(WINTER_SEASON_START_WEEK, year));
      } else {
        ({ startDate, endDate } = getStartAndEndDateFromWeekNumber(currentWeek + 1, year));
      }
      duration = 7;
      break;
    case 'winter_season':
      if (currentWeek < WINTER_SEASON_START_WEEK || currentWeek >= WINTER_SEASON_START_WEEK) {
        startDate = getStartAndEndDateFromWeekNumber(
          WINTER_SEASON_START_WEEK,
          year + (currentWeek >= WINTER_SEASON_START_WEEK ? 1 : 0),
        ).startDate;
        endDate = getStartAndEndDateFromWeekNumber(
          WINTER_SEASON_END_WEEK,
          year + (currentWeek >= WINTER_SEASON_START_WEEK ? 2 : 1),
        ).endDate;
      } else {
        disabled = true;
      }
      duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)); // Duration in days
      break;
    case '1_year':
      if (today < possibleStartDate) {
        today = possibleStartDate;
      }
      startDate = today;
      endDate = new Date(year + 1, today.getMonth(), today.getDate()); // One year later
      duration = 365;
      break;
    default:
      throw new Error('Invalid option');
  }

  const range = createDateRange(startDate, duration, {
    toEndOfYear: false,
    fullWeeks: false,
  });

  return {
    startDate,
    endDate,
    duration,
    range,
    disabled,
  };
};
